import React from 'react';
import styled from 'styled-components';

import { BasketState } from '@front/common/src/ducks/common/basket';
import { formatPrice, formatPricePerHour } from '@front/common/src/utils/formatting';
import colors from '@front/common/src/utils/colors';
import { getCurrentCountry } from '@front/common/src/utils/country';
import { PADDING_BY_SIZE } from '@front/common/src/components/selectable-card/constants';

import { MembershipOptIn } from '@front/customer/src/modals/membership-paywall/utils';
import { useInstantTaxCreditCleaningFunnelVariant } from '@front/customer/src/hooks/use-instant-tax-credit-cleaning-funnel-variant';
import { MEMBERSHIP_CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';
import { PricesResponse } from '@front/customer/src/types/prices';

import { translate } from '@front/i18n';

import Text from '@front/web-ui/src/components/base-text/variants/text';
import Skeleton from '@front/web-ui/src/components/skeleton/skeleton';

import SelectablePriceCard, { SelectablePriceCardProps } from '../selectable-price-card';

const t = translate('modals.membership_paywall.price_cards');

const StyledCard = styled(SelectablePriceCard)`
  height: 105px;

  /*
   The full label adds extra height, but it uses absolute position.
   Therefore we need to add extra padding on top so that the rest of the card is still correctly
   horizontally centred.
  */
  padding-top: calc(${PADDING_BY_SIZE.md}px + 13px);
`;

const StyledPrices = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

type SelectableCleaningMemberPriceCardProps = Pick<SelectablePriceCardProps, 'onPress'> & {
  duration: BasketState['duration'];
  prices: PricesResponse | undefined;
  isLoadingPrices: boolean;
  optInChoice?: MembershipOptIn;
};

const SelectableCleaningMemberPriceCard = ({
  duration,
  onPress,
  prices,
  isLoadingPrices,
  optInChoice,
}: SelectableCleaningMemberPriceCardProps) => {
  const currentCountry = getCurrentCountry();

  const showTaxReductions = useInstantTaxCreditCleaningFunnelVariant({ universe: 'cleaning' });

  const showPrices = !!duration;
  const formattedReduction = showPrices
    ? formatPrice(prices?.member.discount.price)
    : formatPricePerHour(MEMBERSHIP_CONFIG_BY_COUNTRY[currentCountry].cleaningMaxPricePerHour, { precision: 0 });

  return (
    <StyledCard
      isSelected={optInChoice === 'membership'}
      onPress={onPress}
      title={t('.member.pay_with_club')}
      label={t('.member.label', { memberReduction: formattedReduction })}
      hasFullWidthLabel
      value='membership'
    >
      {showPrices && (
        <StyledPrices>
          {isLoadingPrices ? (
            <Skeleton linesWidth={showTaxReductions ? ['70px', '90px'] : ['70px']} />
          ) : (
            <>
              <Text.MDBold color={colors.pink120}>{formatPrice(prices?.member.after_tax.price)}</Text.MDBold>
              {showTaxReductions && (
                <>
                  <Text>{t('.cleaning.or_price', { priceBeforeITC: formatPrice(prices?.member.total.price) })}</Text>
                  <Text.XXS>{t('.cleaning.before_itc')}</Text.XXS>
                </>
              )}
            </>
          )}
        </StyledPrices>
      )}
    </StyledCard>
  );
};

export default SelectableCleaningMemberPriceCard;
